/**
 * PackageContextBar — sticky context bar for GH-252 upload package anchor.
 *
 * Renders above the DropZone on /ingest/upload. Two states:
 *   1. No context selected — shows package select + SourceGroupPicker + skip link.
 *   2. Context selected   — shows anchored summary + Change + Skip buttons.
 *
 * Props:
 *   tenantId        — current tenant (used as localStorage key scope)
 *   onContextChange — called when packageId + sourceGroup are both set
 *   onSkip          — called when user clicks "Skip — standalone batch"
 *
 * localStorage is managed via use-last-package. On mount, restores the stored
 * context and immediately calls onContextChange with it.
 *
 * Reuses SourceGroupPicker from packages/source-group-picker (read-only import).
 */

import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '../../../lib/utils'
import { useAppStore } from '../../../stores/app-store'
import { SourceGroupPicker } from '../../packages/source-group-picker/source-group-picker'
import { useOnboardingPackages } from '../../packages/use-onboarding-packages'
import { useLastPackage } from './use-last-package'
import type { SourceGroup } from '@agentwiki/shared'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface PackageContext {
  packageId: string
  sourceGroup: SourceGroup
}

export interface PackageContextBarProps {
  tenantId: string | null
  /** Called whenever a complete context (packageId + sourceGroup) is set. */
  onContextChange: (ctx: PackageContext) => void
  /** Called when user skips — parent should treat batch as standalone. */
  onSkip: () => void
}

// ── Component ─────────────────────────────────────────────────────────────────

export function PackageContextBar({ tenantId, onContextChange, onSkip }: PackageContextBarProps) {
  const { t } = useTranslation('ingest')
  const { theme } = useAppStore()
  const isDark = theme === 'dark'

  const { context, set: persistContext, clear: clearContext } = useLastPackage(tenantId)

  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(
    context?.packageId ?? null,
  )
  const [selectedSourceGroup, setSelectedSourceGroup] = useState<SourceGroup | null>(
    context?.sourceGroup ?? null,
  )

  // Fetch package list for the selector
  const { data: packagesData } = useOnboardingPackages()
  const packages = packagesData?.items ?? []

  // On mount: if a stored context exists, call onContextChange immediately
  useEffect(() => {
    if (context) {
      onContextChange({ packageId: context.packageId, sourceGroup: context.sourceGroup })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // intentionally run once on mount only

  // Whenever both selections are complete, persist + notify parent
  useEffect(() => {
    if (selectedPackageId && selectedSourceGroup) {
      const ctx: PackageContext = { packageId: selectedPackageId, sourceGroup: selectedSourceGroup }
      persistContext(ctx)
      onContextChange(ctx)
    }
  }, [selectedPackageId, selectedSourceGroup, persistContext, onContextChange])

  function handlePackageChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const val = e.target.value || null
    setSelectedPackageId(val)
    // Reset source group when package changes to avoid stale combination
    if (!val) {
      setSelectedSourceGroup(null)
    }
  }

  function handleChangeContext() {
    setSelectedPackageId(null)
    setSelectedSourceGroup(null)
  }

  function handleSkip() {
    clearContext()
    setSelectedPackageId(null)
    setSelectedSourceGroup(null)
    onSkip()
  }

  const selectedPackageName =
    selectedPackageId
      ? packages.find((p) => p.id === selectedPackageId)?.name ?? selectedPackageId
      : null

  const hasContext = !!selectedPackageId && !!selectedSourceGroup

  const baseClasses = cn(
    'sticky top-0 z-10 flex flex-wrap items-center gap-3 px-4 py-2.5 text-sm',
    'border-b transition-colors',
    isDark
      ? 'border-white/[0.08] bg-surface-1'
      : 'border-neutral-200 bg-white',
  )

  // ── Anchored state ──────────────────────────────────────────────────────────

  if (hasContext) {
    return (
      <div
        className={baseClasses}
        role="region"
        aria-label={t('upload.package.bar_label')}
      >
        <span className={cn('flex-1 text-sm', isDark ? 'text-neutral-300' : 'text-neutral-700')}>
          {t('upload.package.bar_label')}{' '}
          <strong>{selectedPackageName}</strong>
          {' '}
          {t('upload.package.arrow')}
          {' '}
          <strong>{selectedSourceGroup}</strong>
        </span>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleChangeContext}
            className={cn(
              'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
              isDark
                ? 'text-neutral-400 hover:bg-surface-3 hover:text-neutral-200'
                : 'text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700',
            )}
          >
            {t('upload.package.change')}
          </button>

          <button
            type="button"
            onClick={handleSkip}
            className={cn(
              'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
              isDark
                ? 'text-neutral-500 hover:bg-surface-3 hover:text-neutral-300'
                : 'text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600',
            )}
          >
            {t('upload.package.skip')}
          </button>
        </div>
      </div>
    )
  }

  // ── Picker state ────────────────────────────────────────────────────────────

  return (
    <div className={baseClasses} role="region" aria-label={t('upload.package.bar_label')}>
      {/* Package select */}
      <div className="flex flex-col gap-0.5">
        <label
          htmlFor="pkg-ctx-select"
          className={cn(
            'text-[0.625rem] font-semibold uppercase tracking-wide',
            isDark ? 'text-neutral-500' : 'text-neutral-400',
          )}
        >
          {t('upload.package.package_label')}
        </label>
        <select
          id="pkg-ctx-select"
          aria-label={t('upload.package.package_label')}
          value={selectedPackageId ?? ''}
          onChange={handlePackageChange}
          className={cn(
            'rounded-md border px-2 py-1 text-xs font-medium transition-colors',
            'focus:outline-none focus:ring-2 focus:ring-brand-500/50',
            isDark
              ? 'border-white/[0.10] bg-surface-2 text-neutral-200'
              : 'border-neutral-200 bg-white text-neutral-800',
          )}
        >
          <option value="">{t('upload.package.select_package')}</option>
          {packages.map((pkg) => (
            <option key={pkg.id} value={pkg.id}>
              {pkg.name}
            </option>
          ))}
        </select>
      </div>

      {/* Source group picker — reused from packages feature (label is rendered internally) */}
      <SourceGroupPicker
        value={selectedSourceGroup}
        onChange={setSelectedSourceGroup}
        disabled={!selectedPackageId}
      />

      {/* Skip link */}
      <div className="ml-auto">
        <button
          type="button"
          onClick={handleSkip}
          className={cn(
            'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
            isDark
              ? 'text-neutral-500 hover:bg-surface-3 hover:text-neutral-300'
              : 'text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600',
          )}
        >
          {t('upload.package.skip')}
        </button>
      </div>
    </div>
  )
}
